import { TabsPanelId, useSplitView } from '@/app/_common/split-view/split-view-context'
import { createTurnTab } from '@/app/_common/split-view/tabs/factories'
import { TAB_TYPE_CHAT } from '@/app/_common/split-view/tabs/types'
import { ChatInappropriateMessage } from '@/app/chat/_common/chat-notification-banner'
import { MessageIcons } from '@/app/chat/_common/message-icons'
import { Message, MessageData, MessageUserImage } from '@/app/chat/_common/message-types'
import { LazyLottie } from '@/lib/lazy-components'
import { cn } from '@/lib/utils'
import ThinkingAnimation from '@/styles/animations/thinking-animation.json'
import React from 'react'
import { MessageInputAttachments } from './message-input-attachments'
import { MessageOutput } from './message-output'
import { PlanStepsContainer } from './plan-steps/plan-steps-container'
import { useTurnParsed } from './use-turn-parsed'

type TurnMessagesProps = Readonly<{
  workflowId: string
  turnId: string
  chatTitle: string
  activeTurnId: string | null
  className?: string
  style?: React.CSSProperties
}>

const TurnMessages = React.forwardRef<HTMLDivElement, TurnMessagesProps>(
  ({ turnId, workflowId, chatTitle, activeTurnId, className, style }, ref) => {
    const { addTab, getTabPositionMetadata } = useSplitView()
    const {
      outputMessage,
      showLoadingMessage,
      userMessageContent,
      isModerationDomain,
      isInappropriate,
      status,
      error,
      showPlan,
      isLastTurn,
      inputAttachments,
    } = useTurnParsed({ turnId, workflowId })

    const [wasEverActive, setWasEverActive] = React.useState(false)
    const enableAnimation = wasEverActive && isLastTurn

    const handleOpenInNewTabClick = React.useCallback(() => {
      const position = getTabPositionMetadata({ tabType: TAB_TYPE_CHAT })
      const panelId = position?.panelId === TabsPanelId.Right ? TabsPanelId.Left : TabsPanelId.Right
      addTab({
        tab: createTurnTab({ turnId, workflowId }),
        panelId,
      })
    }, [addTab, turnId, workflowId, getTabPositionMetadata])

    React.useEffect(() => {
      if (!wasEverActive && activeTurnId === turnId) {
        setWasEverActive(true)
      }
    }, [activeTurnId, turnId, wasEverActive])

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)} style={style}>
        {typeof userMessageContent === 'string' &&
          (isModerationDomain ? (
            <MessageUserImage text={userMessageContent} />
          ) : (
            <Message messageType='user'>{userMessageContent}</Message>
          ))}
        {typeof userMessageContent === 'object' && (
          <MessageData messageType='user' data={userMessageContent} />
        )}
        {inputAttachments?.length > 0 && (
          <MessageInputAttachments inputAttachments={inputAttachments} />
        )}
        {showLoadingMessage && (
          <LazyLottie className='mt-4 w-24 opacity-70' animationData={ThinkingAnimation} />
        )}
        {isInappropriate && <ChatInappropriateMessage />}
        {showPlan && (
          <PlanStepsContainer
            status={status}
            error={error}
            turnId={turnId}
            enableAnimation={enableAnimation}
            workflowId={workflowId}
          />
        )}
        {outputMessage && (
          <MessageOutput
            content={outputMessage.messageContent.content}
            title={chatTitle}
            enableTypingAnimation={enableAnimation}
            renderIcons={(options) => (
              <MessageIcons
                key={outputMessage.messageId}
                workflowId={workflowId}
                messageId={outputMessage?.messageId}
                feedback={outputMessage?.feedback}
                openInNewTabOptions={{ onOpenInNewTab: handleOpenInNewTabClick }}
                {...options}
              />
            )}
          />
        )}
      </div>
    )
  },
)
TurnMessages.displayName = 'TurnMessages'

export { TurnMessages }
